import React, {useEffect, useRef, useState} from 'react';
import '../ChestPurchaseModal/ChestPurchaseModal.css';
import '../PremiumInfoModal/PremiumInfoModal.css';
import PageButton from '../../elements/PageButton/PageButton';
import {getPremiumImagePath} from '../../../utils/premium/getPremiumImagePath';
import {pluralizeDays} from '../../../utils/other/pluralizeRu';
import {PREMIUM} from '../../../constants/premium';
import {COINS_COLOR} from '../../../styles/constants';
import {MENU_SOUND_MANAGER} from '../../../scenes/MenuSoundManager';

const PAGE_ID = 'PremiumRewardModal';

/**
 * Модалка выпавшего из сундука премиума. Построена на тех же классах, что и
 * модалка покупки премиума в магазине (PremiumInfoModal -> ChestPurchaseModal),
 * с той же картинкой, тем же перечнем преимуществ, той же расцветкой текста и
 * тем же звуком. Отличается подсветкой (coins-color) и кнопкой «Забрать дар»
 * вместо покупки.
 *
 * Премиум к моменту показа уже начислен сервером (см. /api/open-chest) —
 * модалка ничего не начисляет, только показывает результат.
 */
function PremiumRewardModal({days, onClaim, onEntered}) {
  const [visible, setVisible] = useState(false);
  const enteredRef = useRef(false);

  useEffect(() => {
    // Тот же звук, что и у модалки покупки премиума в магазине (там его играет
    // открывающая плашка ShopPremiumItem — здесь модалку никто не открывает
    // кликом, поэтому звук на появлении)
    MENU_SOUND_MANAGER.playPremium();

    const showTimer = setTimeout(() => setVisible(true), 50);
    return () => clearTimeout(showTimer);
  }, []);

  const handleClaim = () => {
    setVisible(false);
    setTimeout(() => {
      onClaim?.();
    }, 300);
  };

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget) {
      handleClaim();
    }
  };

  // Плашку сундука под модалкой убираем только после появления модалки —
  // иначе она исчезала бы на глазах у игрока (тот же прием, что и в
  // FactionUnitSkinRewardModal)
  const handleTransitionEnd = (event) => {
    if (event.target !== event.currentTarget) return;
    if (event.propertyName !== 'opacity') return;
    if (visible && !enteredRef.current) {
      enteredRef.current = true;
      onEntered?.();
    }
  };

  if (!days) return null;

  return (
    <div
      className={`chest-purchase-overlay ${visible ? 'chest-purchase-overlay_visible' : ''}`}
      onClick={handleOverlayClick}
      onTransitionEnd={handleTransitionEnd}
      role="dialog"
      aria-modal="true"
      style={{'--chest-shadow-color': COINS_COLOR}}
    >
      <div
        className={`chest-purchase-modal ${visible ? 'chest-purchase-modal_visible' : ''}`}
      >
        <div className="chest-purchase-content">
          <div className="chest-purchase-image-wrapper">
            <img
              className="chest-purchase-image"
              src={getPremiumImagePath(PREMIUM.MONTH_1)}
              alt="Премиум"
            />
          </div>

          <h2 className="chest-purchase-name text-coins">
            {`Премиум на ${days} ${pluralizeDays(days)}`}
          </h2>

          <div className="premium-info-benefits">
            <div className="premium-info-benefit">
              <img className="premium-info-benefit-icon" src="/img/icons/numens.png" alt="Награда"/>
              <span>
                <span className="premium-info-benefit-accent">х2 награда</span>
                <span className="premium-info-benefit-regular">за все матчи</span>
              </span>
            </div>
            <div className="premium-info-benefit">
              <img
                className="premium-info-benefit-icon premium-info-benefit-icon_league" src="/img/icons/league_gold.png"
                alt="Рейтинговый матч"
              />
              <span>
                <span className="premium-info-benefit-accent">х4 награда</span>
                <span className="premium-info-benefit-regular">за первый рейтинговый матч дня</span>
              </span>
            </div>
            <div className="premium-info-benefit">
              <span
                className="premium-info-benefit-icon premium-info-benefit-icon_chest"
                role="img"
                aria-label="Ежедневные награды"
              />
              <span>
                <span className="premium-info-benefit-accent">Улучшенные ежедневные награды</span>
                <span className="premium-info-benefit-regular">за вход</span>
              </span>
            </div>
            <div className="premium-info-benefit">
              <img className="premium-info-benefit-icon" src="/img/icons/coupon.png" alt="Скидка"/>
              <span>
                <span className="premium-info-benefit-accent">Дополнительная скидка 10%</span>
                <span className="premium-info-benefit-regular">на покупку сундуков за</span>
                <span className="premium-info-benefit-crystals">кристаллы</span>
              </span>
            </div>
          </div>

          <div className="main-menu-buttons chest-purchase-buttons">
            <PageButton onClick={handleClaim} pageId={PAGE_ID} buttonId="claim_reward">
              Забрать дар
            </PageButton>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PremiumRewardModal;
